export type PostHogReleaseEnv = Readonly<Record<string, string | undefined>>;

export type PostHogReleaseProperties = {
  readonly deploy_environment?: string;
  readonly deployment_id?: string;
  readonly git_branch?: string;
  readonly git_commit_sha?: string;
  readonly release_id?: string;
};

const RELEASE_VALUE_MAX_LENGTH = 120;

function firstEnvValue(env: PostHogReleaseEnv, keys: readonly string[]) {
  for (const key of keys) {
    const value = env[key]?.trim();

    if (value) {
      return value;
    }
  }

  return undefined;
}

function normalizeReleaseValue(value?: string) {
  const normalized = value
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9/_:.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, RELEASE_VALUE_MAX_LENGTH);

  return normalized || undefined;
}

export function getPostHogReleaseProperties(env: PostHogReleaseEnv = process.env): PostHogReleaseProperties {
  const gitCommitSha = normalizeReleaseValue(
    firstEnvValue(env, [
      "NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA",
      "VERCEL_GIT_COMMIT_SHA",
      "NEXT_PUBLIC_GIT_SHA",
      "GIT_SHA",
      "COMMIT_SHA",
    ]),
  );
  const releaseId =
    normalizeReleaseValue(firstEnvValue(env, ["NEXT_PUBLIC_SEOH_RELEASE_ID", "SEOH_RELEASE_ID"])) || gitCommitSha;

  return removeEmptyReleaseProperties({
    deploy_environment: normalizeReleaseValue(
      firstEnvValue(env, ["NEXT_PUBLIC_SEOH_DEPLOY_ENV", "SEOH_DEPLOY_ENV", "NEXT_PUBLIC_VERCEL_ENV", "VERCEL_ENV", "NODE_ENV"]),
    ),
    deployment_id: normalizeReleaseValue(
      firstEnvValue(env, [
        "NEXT_PUBLIC_SEOH_DEPLOYMENT_ID",
        "SEOH_DEPLOYMENT_ID",
        "NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID",
        "VERCEL_DEPLOYMENT_ID",
      ]),
    ),
    git_branch: normalizeReleaseValue(
      firstEnvValue(env, ["NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF", "VERCEL_GIT_COMMIT_REF", "NEXT_PUBLIC_GIT_BRANCH", "GIT_BRANCH"]),
    ),
    git_commit_sha: gitCommitSha,
    release_id: releaseId,
  });
}

function removeEmptyReleaseProperties(properties: PostHogReleaseProperties): PostHogReleaseProperties {
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined)) as PostHogReleaseProperties;
}
